import { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import StartIcon from '@mui/icons-material/PlayArrow';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import planConfigService from "@plannel/services/data/plan-config-service";
import dataTransformService from "@plannel/services/data/data-transform-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import userService from "@plannel/services/system/user-service";
import debug from "@plannel/utils/debug-util";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";

import { useTranslation, withTranslation } from "react-i18next";
import dateUtils from "@plannel/utils/date-util";
import codeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { cloneDeep, includes, isEmpty, isEqual, size, uniq } from "lodash";
import { Checkbox, MenuItem, Select } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import AppCard, { CARD_HEADER_SX, SECTION_TITLE_SX } from "@plannel/components/ui/AppCard";
import { FS } from "@plannel/utils/font-util";

/* 색은 ColorUtil 에 모아 두고 여기서는 참조만 한다(색 인라인 금지 규약). */
const { dataTransformOptionChip: OPTION_COLOR } = ColorUtil;

/*
 * 값 텍스트의 굵기.
 * 다른 화면의 그리드는 ag-grid balham 기본 폰트(Helvetica Neue)로 그려지는데,
 * 이 화면은 그리드를 걷어내 테마 폰트(Noto Sans KR)로 그려진다. 같은 400 이라도 Noto 쪽이 훨씬 가늘어
 * 나란히 놓고 보면 이 화면만 흐릿해 보인다. 그래서 값·라벨은 500 으로 올려 무게를 맞춘다.
 * (public/index.html 이 Noto Sans KR 300/400/500/700 을 받아오므로 500 은 합성이 아닌 실제 웨이트다.)
 */
const VALUE_WEIGHT = 500;

/* 값에 딸린 보조 텍스트(최신일자·개수·선택 수)의 공통 톤.
   AppCard 규약대로 제목보다 작고 흐리게만 둔다 — 대문자·자간 확대는 쓰지 않는다(다른 화면과 톤이 튄다). */
const DETAIL_TEXT_SX = {
  fontSize: FS.TITLE,
  fontWeight: VALUE_WEIGHT,
  color: "text.secondary",
};

/*
 * 좁은 배치로 넘어가는 기준 폭 — 넓은 배치가 요구하는 값(레일 245 + 소스 330 + 표 980 + 여백 85)이다.
 *
 * Full HD 노트북이라도 Windows 배율이 150% 면 브라우저가 보는 폭은 1280px 뿐이라
 * (125% → 1536, 175% → 1097) 이 배치가 들어가지 않는다. 그때 표의 절반이 가로 스크롤 뒤로 숨고,
 * End Date 열이 하필 잘리는 경계에 걸려 달력 아이콘을 누를 수 없었다(화면을 50% 로 축소해야 눌렸다).
 */
const WIDE_MIN_WIDTH = 1640;

/*
 * 좁은 배치조차 들어가지 않는 폭 — 레일 245 + 소스 카드 240 + 여백 80 + 표 840.
 * 이 밑에서는 시작·종료 날짜를 한 열에 위아래로 포갠다(자세한 이유는 COLS_DENSE 참고).
 *
 * ⚠️ 레일 245 가 더해져 있다. 예전에는 이 구간에서 레일이 위로 눕어 가로 폭을 안 썼기에
 *   240+80+840 = 1160 이었다. 레일을 항상 왼쪽 세로로 두기로 하면서(ProgressRail 주석)
 *   그 245 를 도로 계산에 넣지 않으면, 표가 245px 만큼 모자란 채로 "아직 넉넉하다"고 판정돼
 *   열이 가로 스크롤 뒤로 숨는다 — 85676a5a28 이 고쳤던 바로 그 증상이다.
 */
const COMPACT_MIN_WIDTH = 1405;

/*
 * 소스 카드를 세로 띠로 접는 폭 — 레일 245 + 소스 240 + 여백 80 + 표 660.
 * 사이드바를 펼친 채 Agent Chat 까지 열면 본문이 여기까지 내려간다.
 * 접힌 뒤에도 띠의 버튼으로 언제든 펼칠 수 있고, 그때는 표 위에 겹쳐 떠서 표 폭을 뺏지 않는다.
 * (레일 245 를 더한 이유는 COMPACT_MIN_WIDTH 주석 참고.)
 */
const SOURCE_COLLAPSE_WIDTH = 1225;

/* 접힌 소스 카드(세로 띠)�� 폭. 이 폭이면 표는 본문 784px 까지 스크롤 없이 버틴다. */
const SOURCE_STRIP_WIDTH = 44;

const LAYOUT = { WIDE: "wide", COMPACT: "compact", DENSE: "dense" };

/*
 * 뷰포트가 아니라 **이 화면이 실제로 받은 폭**으로 판정한다.
 * Agent Chat 패널이나 펼친 사이드바는 뷰포트를 그대로 둔 채 본문만 좁히므로 미디어 쿼리로는 못 잡는다.
 */
const useLayoutMode = (ref) => {
  const [mode, setMode] = useState({ layout: LAYOUT.WIDE, sourceCollapsed: false });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(([entry]) => {
      const { width } = entry.contentRect;
      const next = {
        layout: width >= WIDE_MIN_WIDTH ? LAYOUT.WIDE : (width >= COMPACT_MIN_WIDTH ? LAYOUT.COMPACT : LAYOUT.DENSE),
        sourceCollapsed: width < SOURCE_COLLAPSE_WIDTH,
      };
      /* 창을 끄는 동안 매 프레임 발화한다 — 경계를 넘을 때만 다시 그리도록 같은 값이면 이전 객체를 그대로 돌려준다. */
      setMode((prev) => (
        prev.layout === next.layout && prev.sourceCollapsed === next.sourceCollapsed ? prev : next
      ));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return mode;
};

/* 소스 카드 폭. 좁은 배치에서는 타겟 표에 폭을 넘긴다 —
   이 카드는 읽기 전용 참고 목록이라 조금 좁아져도 이름과 최신일자는 그대로 읽힌다. */
const SOURCE_CARD_WIDTH = 330;
const SOURCE_CARD_WIDTH_COMPACT = 240;

/* 타겟 표가 요구하는 최소 폭. 좁은 배치는 최신일자 열을, 더 좁은 배치는 날짜 한 열을 더 덜어낸 값이다. */
const TABLE_MIN_WIDTH = 980;
const TABLE_MIN_WIDTH_COMPACT = 840;
const TABLE_MIN_WIDTH_DENSE = 660;

/* 타겟 목록의 열 폭(남는 폭을 나눠 갖는 비율). 헤더와 각 행이 같은 값을 써야 세로로 줄이 맞는다.
   시작·종료 날짜 열은 비율이 아니라 고정 폭이다 — 아래 DATE_FIELD_WIDTH 참고. */
const COLS = {
  check: "44px",
  name: 2.4,
  uom: 0.9,
  option: 1.5,
  cust: 0.9,
  item: 0.9,
  latest: 1.3,
};

/*
 * 좁은 배치의 열 비율.
 * 최신일자는 열을 빼고 이름 밑 캡션으로 내린다 — 읽기만 하는 값이라 열을 하나 차지할 자리값이
 * 편집 컨트롤(단위·옵션·레벨)보다 낮다. 그 열을 덜어낸 폭을 나머지에 나눠 줘서
 * 셀렉트가 화살표만 남는 폭(55px)까지 눌리지 않게 한다.
 */
const COLS_COMPACT = {
  check: "44px",
  name: 2.4,
  uom: 1.0,
  option: 1.5,
  cust: 1.05,
  item: 1.05,
};

/*
 * 셀 안쪽 왼쪽 들여쓰기.
 * 편집 컨트롤(Select·DatePicker)은 hover 배경이 값에 딱 붙지 않도록 자기 안에 여백을 갖는데,
 * 그냥 텍스트로 그리는 칸(고정 UOM·대시·헤더)에 같은 값이 없으면 한 열 안에서 값 시작점이 어긋난다
 * (예: 'Month' 는 0, '3Months' 셀렉트는 6px). 그래서 한 곳에서 정하고 양쪽에 똑같이 준다.
 */
const CELL_INSET = 0.75;

/*
 * 날짜 입력의 폭 — 그리고 그 입력이 든 열의 폭.
 * 날짜 입력만은 픽셀 고정이라(글자 수가 'yyyy-MM-dd' 로 정해져 있다) 열을 비율로 두면
 * 창이 좁을 때 열이 입력보다 좁아지고, 입력이 셀 밖으로 삐져나와 달력 아이콘이 옆 열을 덮었다
 * (브라우저를 확대하면 CSS 뷰포트가 좁아져 그 폭에 걸린다).
 * 그래서 이 두 열만 비율에서 빼고 입력과 같은 고정 폭으로 둔다 — 좁아져도 어긋나지 않는다.
 */
const DATE_FIELD_WIDTH = 128;

/*
 * 컬럼 라벨 — 값보다 물러나게 둔다.
 * 대문자·자간 확대는 쓰지 않는다(AppCard 규약). 앱의 다른 표는 모두 ag-grid balham 헤더라
 * 여기만 대문자로 두면 같은 화면 안에서 이 표만 다른 ���스템처럼 읽힌다.
 * 라벨 문구는 언어팩이 이미 Title Case 로 갖고 있어 그대로 쓰면 된다("Target Data Name").
 */
const HEADER_LABEL_SX = {
  fontSize: FS.BODY,
  fontWeight: VALUE_WEIGHT,
  color: "text.secondary",
  pl: CELL_INSET,
};

/* flex 행 안의 셀은 기본 min-width:auto 때문에 내용물이 길면 옆 열을 밀 수 있다.
   각 셀이 자기 비율 안에서 줄어들 수 있게 0 으로 열어둔다. */
const FLEX_CELL_SX = { minWidth: 0 };

/* 날짜 열만 예외 — 비율로 나누지 않고 입력과 같은 폭으로 고정한다(줄지도 늘지도 않는다).
   헤더와 본문 양쪽에 같은 값을 줘야 줄이 맞는다. */
const DATE_CELL_SX = { width: DATE_FIELD_WIDTH, flexShrink: 0 };

/* 값이 곧 입력칸이 되도록, 컨트롤을 평소엔 텍스트처럼 두고 hover/focus 에서만 배경을 준다.
   테두리 있는 입력칸을 7행 × 5개 늘어놓으면 설정 목록이 아니라 폼처럼 무거워진다. */
const QUIET_SELECT_SX = {
  fontSize: FS.TITLE,
  fontWeight: VALUE_WEIGHT,
  /* 열 폭만큼 늘리지 않는다 — 늘리면 화살표가 값에서 떨어져 다음 열 옆에 붙고,
     그 열의 컨트롤인 것처럼 보인다(옵션 화살표가 고객레벨 것으로 읽히던 문제). */
  maxWidth: "100%",
  "&:before, &:after": { display: "none" },
  "& .MuiSelect-select": {
    py: 0.25,
    pl: 0.75,
    /* 기본 우측 여백(24~32px)은 값과 화살표를 멀리 떼어놓는다 — 화살표 자리만 남긴다. */
    pr: "20px !important",
    borderRadius: 1,
    boxSizing: "border-box",
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  "& .MuiSelect-icon": { right: 0 },
  "&:hover .MuiSelect-select": { bgcolor: "action.hover" },
};

/* 기간 입력도 값처럼 보이게 — 밑줄을 지우고 hover 에서만 배경을 준다. */
const PERIOD_DATE_SX = {
  width: DATE_FIELD_WIDTH,
  "& .MuiInputBase-root": { fontSize: FS.TITLE, fontWeight: VALUE_WEIGHT },
  "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
  "& .MuiInputBase-input": { py: 0.25, px: 0.75, borderRadius: 1 },
  "&:hover .MuiInputBase-input": { bgcolor: "action.hover" },
  /* 달력 아이콘까지 열마다 또렷하면 값보다 아이콘이 먼저 보인다. 그렇다고 감춰 버리면
     날짜가 그냥 글자로 보여 고칠 수 있다는 걸 알 수 없다 — 평소엔 흐리게 두고 hover 에서만 또렷하게. */
  "& .MuiInputAdornment-root": { opacity: 0.35, transition: "opacity 120ms" },
  "&:hover .MuiInputAdornment-root": { opacity: 1 },
};

/* 옵션은 값이라기보다 상태에 가까워 틴트 칩으로 감싼다. */
const OPTION_CHIP_SX = {
  fontSize: FS.TITLE,
  fontWeight: VALUE_WEIGHT,
  maxWidth: "100%",
  color: OPTION_COLOR.text,
  "&:before, &:after": { display: "none" },
  "& .MuiSelect-select": {
    py: 0.25,
    pl: CELL_INSET,
    pr: "20px !important",
    borderRadius: 1,
    boxSizing: "border-box",
    minWidth: 0,
    bgcolor: OPTION_COLOR.bg,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  "& .MuiSelect-icon": { right: 0, color: OPTION_COLOR.text },
};

/* 행을 구분하는 건 격자선이 아니라 헤어라인 하나뿐. 선택된 행만 primary 틴트로 든다. */
const ROW_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1,
  minHeight: 48,
  borderBottom: 1,
  borderColor: "divider",
  "&:last-of-type": { borderBottom: "none" },
};

const rowKeyOf = (row) => `${row.title}:${row.target}`;

/*
 * 화면에 보이는 이름은 state 에 넣지 않고 그릴 때 만든다.
 * 번역된 문자열을 행에 굳혀 두면 언어를 바꿔도 state 가 그대로라 새로 조회할 때까지 옛 언어가 남는다
 * (행에는 번역 키인 title/target 만 담고, 번역은 렌더에서 한다).
 */
const targetNameOf = (t, row) => (
  row.title === demandTitle ? `${t("measure")} - ${t(row.target)}` : t(row.target)
);

/* 평균 판매실적의 기간단위 표기. 서버는 여기서 숫자만 뽑아 쓰므로(DataTransformService 의 replaceAll)
   글자 부분이 언어마다 달라도 되고, 화면과 전송값이 같은 함수를 쓰게 한 곳에 둔다. */
const avgUomLabel = (t, months) => `${months}${t("months")}`;

/*
 * Run 확인창 본문 — 지금 무엇이 어떤 설정으로 돌아가는지.
 *
 * 왜 화면이 아니라 확인창인가: 이 화면은 폭이 늘 모자라 표가 1160px 아래에서 열을 접고
 * 980px 아래에서는 소스 카드까지 세로 띠로 접힌다(WIDE_MIN_WIDTH 주석). 요약을 상시 노출하면
 * 그 폭을 표에서 또 빼앗는다. 확인창은 폭을 하나도 안 쓰고, 되돌릴 수 없는 실행 **직전**이라
 * 확인의 값어치가 가장 큰 자리이기도 하다.
 *
 * 값은 고르는 화면에서 만든 그대로 받는다(displayInfoOf) — 여기서 코드값을 다시 번역하지 않는다.
 */
const RunSummary = ({ t, rows, hasAddOption }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
    <Typography sx={{ fontSize: FS.TITLE, color: "text.secondary" }}>
      {t("MSG_ConfirmRunTransform", { count: rows.length })}
    </Typography>

    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 280, overflowY: "auto" }}>
      {rows.map((row) => {
        const d = row.displayInfo ?? {};
        /* 기간이 없는 타겟도 있다(전량 변환) — 그때는 기간 칸을 만들지 않는다.
           "— ~ —" 로 채우면 날짜를 덜 고른 것처럼 읽힌다. */
        const period = d.startDate || d.endDate ? `${d.startDate ?? "—"} ~ ${d.endDate ?? "—"}` : null;
        const meta = [d.uom, period, d.option, d.custLevel, d.itemLevel].filter(Boolean).join(" · ");
        return (
          <Box key={rowKeyOf(row)} sx={{ px: 1.25, py: 1, borderRadius: 1, bgcolor: "action.hover" }}>
            <Typography sx={{ fontSize: FS.TITLE, fontWeight: VALUE_WEIGHT, color: "text.primary" }}>
              {d.name}
            </Typography>
            {meta && (
              <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", mt: 0.25, lineHeight: 1.5 }}>
                {meta}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>

    {/* 기존 add 옵션 경고를 여기로 합쳤다 — 확인창이 둘로 나뉘면 두 번째 창에서는 무엇을 고쳤는지 안 보인다.
        지우고 다시 넣는 다른 옵션과 달리 add 는 기존 데이터에 얹으므로, 경고는 목록 **아래**에 둔다
        (무엇이 걸렸는지 보고 나서 읽어야 뜻이 닿는다). */}
    {hasAddOption && (
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", pt: 0.25 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "warning.main", mt: "7px", flexShrink: 0 }} />
        <Typography sx={{ fontSize: FS.BODY, color: "warning.dark", lineHeight: 1.6 }}>
          {t("MSG_AddDataTransform")}
        </Typography>
      </Box>
    )}
  </Box>
);

/* 고를 수 없는 칸은 빈칸이 아니라 대시로 둔다 — 빈칸은 "값이 없음"인지 "아직 안 채움"인지 구분이 안 된다. */
const Dash = () => <Typography sx={{ fontSize: FS.TITLE, fontWeight: VALUE_WEIGHT, color: "text.disabled", pl: CELL_INSET }}>—</Typography>;

/*
 * 목록이 비었을 때 자리를 지키는 문구.
 * 헤더 줄만 남겨 두면 "아직 못 불러온 건지, 조회는 끝났는데 없는 건지" 구분이 안 된다.
 * 그래서 조회가 끝나기 전에는 아무 말도 하지 않는다 — 없다고 단정하는 건 응답을 받은 뒤부터다.
 */
const EmptyList = ({ loaded, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: 120, p: 3 }}>
    {loaded && (
      <Typography sx={{ fontSize: FS.TITLE, fontWeight: VALUE_WEIGHT, color: "text.disabled" }}>{label}</Typography>
    )}
  </Box>
);

const userData = JSON.parse(localStorage.getItem("user"));

/* posActualFlg가 꺼져 있을 때 dpTargetMeasures 에서 빼는 POS 계열 measure 코드 목록. */
const posTargetMeasureCds = ["POS_SA_QTY", "POS_YOY_QTY", "POS_ASA_QTY"];

const dpTargetMeasures = {
  SA_QTY: "actualSales",
  YOY_QTY: "salesActualYoY",
  FF_QTY: "financePlan",
  SO_QTY: "salesOrder",
  SALES_PLAN_QTY: "salesPlanMeasure",
  ASA_QTY: "avgSalesActual",
  POS_SA_QTY: "posActualSales",
  POS_YOY_QTY: "posSalesActualYoY",
  POS_ASA_QTY: "posAvgSalesActual"
}

const SOURCE = "source";
const TARGET = "target";
const demandTitle = "demandMeasure";
const salePlanTitle = "salesPlan";

const sourceTarget = {
  inventoryAggMon: ["inventoryTransaction", "shipmentTransaction", "salesTransaction"],
  defaultMeasures: ["salesTransaction"],
  planMeasures: ["financePlan"],
  salesPlan: ["salesPlan"],
  salesOrder: ["salesOrder"],
  posMeasures: ["posTransaction"]
}

const DP_PLAN_TRANSFORM = "dpPlanTransform";
const INVENTORY_AGG_MON = "inventoryAggMon";

const { SCENARIO } = codeUtil.dpVersion;
const { PLANNING_RANGE, START_DAY, BUCKET_UNIT, AVG_FLG, POS_ACTUAL_FLG } = codeUtil.dpConfig;
const { FF_QTY, YOY_QTY, MF_QTY, ASA_QTY, POS_SA_QTY, POS_YOY_QTY, POS_ASA_QTY } = codeUtil.dpMeasure;
const { item, customer } = codeUtil;
const { FINANCE_PLAN, SALES_PLAN, SALES_ORDER, SALES_PLAN_MEASURE, AVG_MEASURE, POS_ACTUAL_SALES, POS_ACTUAL_SALES_YOY, POS_AVG_MEASURE } = codeUtil.dpTransformMeasure;
const { snackBarMessageColor } = ColorUtil;
const { APP_DP } = codeUtil.roles;

const DataTransform = ({ t, title, viewName }) => {
  const [isLoading, setIsLoading] = useState(false);
  /* 배치 판정은 화면 루트의 실제 폭으로 한다(WIDE_MIN_WIDTH 주석 참고). */
  const pageRef = useRef(null);
  const { layout, sourceCollapsed } = useLayoutMode(pageRef);
  const compact = layout !== LAYOUT.WIDE;
  const dense = layout === LAYOUT.DENSE;
  /* 접힌 소스 카드를 잠깐 펼쳐 둔 상태. 폭이 다시 넓어지면 카드가 제자리로 돌아가므로 그때는 쓰이지 않는다. */
  const [sourceOpen, setSourceOpen] = useState(false);
  const buckets = codeUtil.buckets;
  const [userRoles, setUserRoles] = useState([]);
  const [progressOn, setProgressOn] = useState(false);
  const [step, setStep] = useState(0);
  const stepRef = useRef(step);
  const dataTransformProcessingCount = useRef(0);
  stepRef.current = step;
  const [errorStep, setErrorStep] = useState(false);

  /* 변환 실행 중에는 선택이 바뀌어도 진행 판정 기준이 흔들리면 안 되므로,
     실행 시작 시점의 선택 개수를 ref 에 고정해두고 비동기 콜백에서 그것만 본다. */
  const runningTargetCount = useRef(0);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [allSourceData, setAllSourceData] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState([]);
  const [targetData, setTargetData] = useState([]);
  /* 카드 조회가 한 번이라도 끝났는지. 목록이 비었을 때 "아직 못 불러온 것"과 "정말 없는 것"을
     가르는 유일한 단서라, 성공·실패 상관없이 응답이 돌아온 시점에 켠다. */
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [planningRange, setPlanningRange] = useState(6);
  const startDay = useRef(codeUtil.dayOfWeek.MONDAY.cd);
  const [bucketUnit, setBucketUnit] = useState(buckets.MONTH);
  const [avgMonths, setAvgMonths] = useState(3);
  const [posActualFlg, setPosActualFlg] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const stackSnackbar = useRef({});

  const getRequestParams = (targets) => {
    const paramsList = [];
    const dpList = [];
    for (const target of targets) {
      const params = {};
      if (target.title === demandTitle || target.title === salePlanTitle) {
        params.startDate = target.startDate;
        params.endDate = target.endDate;
        params.option = target.option;
        params.target = target.title === demandTitle || target.title === salePlanTitle ? target.target : target.title;
        if (target.target === FINANCE_PLAN || target.target === SALES_PLAN || target.target === SALES_PLAN_MEASURE) {
          params.itemLevelCd = target.itemLevel;
          params.custLevelCd = target.custLevel;
        }
        if (target.target === AVG_MEASURE || target.target === POS_AVG_MEASURE) {
          params.uom = avgUomLabel(t, target.avgMonths);
        }
        dpList.push(params);
      } else {
        params.startDate = target.startDate;
        params.endDate = target.endDate;
        /* 월 버킷 종료일은 1일로 스냅되므로 그 달 말일로 되돌려 보낸다.
           값이 Date 객체일 수 있어 isEmpty 로 판정하면 안 된다 — lodash isEmpty(Date) 는 항상 true 라
           변환이 통째로 건너뛰어진다. 이미 말일이어도 같은 값이 나오므로(연·월만 읽는다) 반복 적용해도 안전하다. */
        if (params.endDate) {
          const dateCaste = new ZDate(params.endDate);
          params.endDate = new ZDate(dateCaste.getFullYear(), dateCaste.getMonth() + 1, 0);
        }
        params.target = target.title;
        params.option = target.option;
        paramsList.push(params);
      }
    }
    paramsList.push(dpList);
    return paramsList;
  };

  const getCardDatas = () => {
    dataTransformService.get()
      .then((response) => {
        const { data } = response;
        const { results } = data;
        const targetCardData = results.filter((source) => {
          return source.type === TARGET;
        });
        const sourceCardData = results.filter((source) => {
          return source.type === SOURCE;
        });
        results.forEach((result) => {
          result.latestDate = result?.latestDate ? new ZDate(result.latestDate) : null;
        });
        setAllSourceData(sourceCardData);
        setTargetData(targetCardData);
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setCardsLoaded(true);
      })
  };

  /* Axios 호출 response의 후 처리는 동일한 로직을 사용함으로 then, catch, finally 로직을 공통화 */
  /* responseDate: Integer
  *  multipleTargetFlg: Boolean
  */
  const executeFinallyProcess = (option) => {
    let message = "";
    let emptyCount = 0;
    for (const type of Object.keys(stackSnackbar.current)) {
      if (stackSnackbar.current[type] && stackSnackbar.current[type] > 0) {
        message += `${t(type)}: ${stackSnackbar.current[type]} ${t("MSG_SucessTransform")} \n`;
      } else {
        if (option === "delete") {
          message += `${t(type)}: ${t("MSG_SucessTransform")} \n`;
        } else {
          emptyCount++;
          message += `${t(type)}: ${t("MSG_TransformNoResult")} \n`;
        }
      }
    }
    if (emptyCount === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: message });
    } else if (emptyCount === dataTransformProcessingCount.current) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: message });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: message });
    }
    setStep(4);
    setProgressOn(false);
    setIsLoading(false);
  }

  const executeResultProcess = (target, responseData, multipleTargetFlg, option) => {
    dataTransformProcessingCount.current = dataTransformProcessingCount.current + 1;
    const allProcessed = dataTransformProcessingCount.current === runningTargetCount.current;
    if (responseData && responseData > 0) {
      if (multipleTargetFlg) {
        if (allProcessed) {
          setStep(3);
        }
      } else {
        setStep(3);
      }
    } else {
      if ((multipleTargetFlg && allProcessed) || !multipleTargetFlg) {
        if (option !== "delete") {
          setErrorStep(true);
        }
      }
    }
    stackSnackbar.current[target] = responseData;
    if (allProcessed || !multipleTargetFlg) {
      executeFinallyProcess(option);
    }
  }

  const isAxiosNoResponseError = (e) => Boolean(e?.isAxiosError) && !e?.response;

  const getTransformErrorContent = (target, e) => {
    const noResponse = isAxiosNoResponseError(e);
    const lines = [`${t(target)}: ${t(noResponse ? "MSG_TransNoResponse" : "MSG_StartTransFailed")}`];
    lines.push(t("MSG_CheckTransformHistory"));

    return lines.join("\n");
  }

  const executeCatchProcess = (target, e) => {
    /* 서버가 응답하지 않은 경우는 Axios 전송 오류로 한정한다.
       2xx 응답 후 로컬 파싱/처리 예외는 실패로 분류해야 한다. */
    const noResponse = isAxiosNoResponseError(e);
    const responded = !noResponse;
    if (responded) {
      setErrorStep(true);
    }
    setSnackInfo({
      open: true,
      severity: responded ? snackBarMessageColor.saveFail : snackBarMessageColor.warning,
      content: getTransformErrorContent(target, e)
    });
    debug(e);
    if (dataTransformProcessingCount.current < 1) {
      setProgressOn(false);
    }
  }

  const executeTransform = async () => {

    /* Monthly PSI와 Demand를 동시에 데이터 변환을 하는경우 제어하기 위한 Flag 및 success count 변수입니다.
    *  [InventoryFlg, demandFlg, multipleTargetFlg, dataTransformProcessingCount(useRef)]
    *  해당 변수들은 async의 특성상 순차적으로 실행되지 않기 때문에, 각각의 데이터 변환 성공여부를 체크하여 Step 및 SnackBar를 제어합니다.
    */
    const targetSelectRows = selectedRowData;
    const multipleTargetFlg = targetSelectRows.length > 1;
    dataTransformProcessingCount.current = 0;
    runningTargetCount.current = targetSelectRows.length;
    stackSnackbar.current = {};

    setProgressOn(true);
    setErrorStep(false);
    const paramsList = getRequestParams(targetSelectRows);
    setStep(1);

    setTimeout(() => {
      if (stepRef.current <= 1) {
        setStep(2);
        if (!multipleTargetFlg || (multipleTargetFlg && dataTransformProcessingCount.current === 0)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_StartTransSuccess") });
        }
      }
    }, 6000)

    for (const params of paramsList) {
      if (params.target === INVENTORY_AGG_MON) {
        try {
          const response = await dataTransformService.transform(params);
          const { data } = response;
          const result = data.data;
          executeResultProcess(params.target, result[params.target]?.data ?? -1, multipleTargetFlg, result[params.target]?.option);
        } catch (e) {
          executeCatchProcess(params.target, e);
        }
      } else {
        if (params.length > 0) {
          try {
            const response = await dataTransformService.transformDP(params);
            const { data } = response;
            const result = data.data;
            for (const type in result) {
              if (Object.prototype.hasOwnProperty.call(result, type)) {
                executeResultProcess(type, result[type]?.data, multipleTargetFlg, result[type]?.option)
              }
            }
          } catch (e) {
            executeCatchProcess(DP_PLAN_TRANSFORM, e);
          }
        }
      }
    }
    setSelectedTarget(targetSelectRows);
  };

  const getPeriodUom = () => {
    const param = {
      moduleCd: codeUtil.module.DP,
      activeFlg: true
    };
    planConfigService.getAll(param)
      .then(response => {
        if (response.data) {
          const resultData = response.data.results;
          for (const result of resultData) {
            if (result.configCd === START_DAY) {
              startDay.current = result.strVal ? result.strVal : codeUtil.dayOfWeek.MONDAY.cd;
            } else if (result.configCd === PLANNING_RANGE) {
              setPlanningRange(result.numVal)
            } else if (result.configCd === BUCKET_UNIT) {
              setBucketUnit(result.strVal);
            } else if (result.configCd === AVG_FLG) {
              setAvgMonths(result.numVal)
            } else if (result.configCd === POS_ACTUAL_FLG) {
              setPosActualFlg(result.boolVal ?? false);
            }

          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userData.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  useEffect(() => {
    retrieveUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (isLoading && userRoles) {
      getCardDatas();
    }
    getPeriodUom();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (selectedRowData.length > 0) {
      setAllSourceData(allSourceData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowData]);


  const handleDialog = action => {
    if (action === "SAVE") {
      executeTransform();
    }
  };

  const checkData = () => {
    const targetSelectRows = selectedRowData;
    if (size(targetSelectRows) <= 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_NoSelectedSourceData") });
      return;
    }
    let dateErrorFlg = false;
    let addOptionFlg = false;
    for (const row of targetSelectRows) {
      let startDate = new ZDate(row.startDate)
      let endDate = new ZDate(row.endDate)
      if (codeUtil.isWeekBucket(row.uom)) {
        if (startDate > endDate) {
          dateErrorFlg = true;
        }
      } else {
        startDate = dateUtils.startOfMonthDate(startDate)
        endDate = dateUtils.endOfMonthDate(endDate);
        if (startDate > endDate) {
          dateErrorFlg = true;
        }
      }
      if (row.option === "add") {
        addOptionFlg = true;
      }
    }
    if (dateErrorFlg) {
      setErrorStep(true);
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_SetEndDate") });
      return;
    }
    /*
     * 예전에는 add 옵션일 때만 확인창을 띄웠다. 이제는 **항상** 띄우고 고른 내용을 보여 준다 —
     * 변환은 되돌릴 수 없고(옵션에 따라 기존 데이터를 지운다), 행마다 기간·옵션·레벨을 따로 정하는데
     * 그 값들이 넓은 표에 흩어져 있어 Run 직전에 전체를 한 번에 볼 자리가 없었다.
     */
    setDialogInfo({
      open: true,
      title: "runTransform",
      /* 동작 문자열은 "SAVE" 로 둔다 — handleDialog 가 그걸로 실행을 가른다(제목만 바뀌었다). */
      actionName: "save",
      content: <RunSummary t={t} rows={targetSelectRows} hasAddOption={addOptionFlg} />,
    });
  }

  return (
    /*
     * 진행 레일은 **항상 왼쪽 세로**다(사용자 지정).
     * 한때는 좁으면 위로 눕혀 245px 을 표에 넘겼는데, 자리가 폭에 따라 옮겨 다니는 게
     * 더 혼란스럽다는 판단이다. 대신 그 245px 을 COMPACT_MIN_WIDTH·SOURCE_COLLAPSE_WIDTH 에
     * 도로 더해 뒀다 — 안 그러면 표가 모자란 폭을 넉넉하다고 오판한다(그쪽 주석 참고).
     */
    <Box id="data-transform-page" ref={pageRef}
      sx={{ boxSizing: "border-box", display: "flex", flexDirection: "row", height: "calc(100vh - 125px)", width: "100%" }}>
      <ProgressRail step={step} errorStep={errorStep} />

      {/* 옅은 캔버스 위에 카드를 얹어 카드가 배경에서 떠 보이게 한다.
          position:relative — 접힌 소스 카드를 펼치면 이 캔버스 위에 겹쳐 뜬다. */}
      <Box sx={{ position: "relative", flex: 1, minWidth: 0, minHeight: 0, boxSizing: "border-box", display: "flex", gap: 2, p: 2, bgcolor: "grey.100" }}>
        <DataSourceCard
          t={t}
          loaded={cardsLoaded}
          sourceData={allSourceData}
          posActualFlg={posActualFlg}
          selectedRowData={selectedRowData}
          compact={compact}
          collapsed={sourceCollapsed}
          open={sourceOpen}
          onToggle={setSourceOpen}
        />
        <TargetDataCard
          t={t}
          loaded={cardsLoaded}
          buckets={buckets}
          planningRange={planningRange}
          startDay={startDay.current}
          bucketUnit={bucketUnit}
          avgMonths={avgMonths}
          posActualFlg={posActualFlg}
          targetData={targetData}
          selectedTarget={selectedTarget}
          sourceData={allSourceData}
          selectedRowData={setSelectedRowData}
          userRoles={userRoles}
          onRun={checkData}
          progressOn={progressOn}
          compact={compact}
          dense={dense}
        />
      </Box>

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      {/* maxWidth sm — 기본값 xs(444px)면 요약 한 줄("월 · 2026-01 ~ 2026-12 · 삭제 후 추가 · 전사 · 전사")이
          두 줄로 접힌다. 줄이 접히면 어느 값이 어느 타겟 것인지 눈으로 잇기 어려워진다. */}
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        maxWidth="sm"
        onHandler={handleDialog}
      />
    </Box>
  )
};

/* 세로 한 가지만 그린다 — 가로(눕힌) 분기는 레일을 왼쪽 고정으로 바꾸면서 걷어냈다(호출부 주석). */
const ProgressRail = ({ step, errorStep }) => {
  const { t } = useTranslation();

  const steps = [
    t("transformStep01"),
    t("transformStep02"),
    t("transformStep03"),
    t("transformStep04"),
  ];

  const progressStep = (index) => {
    return ((index === step) && index !== 0 && !errorStep);
  }

  const labelSx = {
    "& .MuiStepLabel-label": { fontSize: FS.TITLE, fontWeight: VALUE_WEIGHT, lineHeight: 1.4 },
    "& .MuiStepLabel-label.Mui-active": { fontWeight: 600 },
  };

  const RailStepLabel = ({ label, index }) => {
    /* 실패는 현재 단계에만 표시한다 — 전 단계를 빨갛게 칠하면 어디서 멎었는지 알 수 없다. */
    const hasError = errorStep && index === step;
    const optional = index === step
      ? <Typography sx={{ ...DETAIL_TEXT_SX, fontSize: FS.SUB, color: "primary.main" }}>{t("inProgress")}</Typography>
      : null;

    if (progressStep(index)) {
      return <StepLabel icon={<CircularProgress size={"22px"} />} error={hasError} optional={optional} sx={labelSx}>{label}</StepLabel>
    }

    return <StepLabel error={hasError} optional={optional} sx={labelSx}>{label}</StepLabel>
  };

  const stepList = steps.map((label, index) => (
    <Step key={label}>
      <RailStepLabel label={label} index={index} />
    </Step>
  ));

  return (
    <Box sx={{ width: 245, flexShrink: 0, boxSizing: "border-box", px: 2.5, py: 2.5, borderRight: 1, borderColor: "divider", overflowY: "auto" }}>
      <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>
        {t("progress")}
      </Typography>
      <Stepper activeStep={step} orientation="vertical">
        {stepList}
      </Stepper>
    </Box>
  );
};

const DataSourceCard = ({ t, loaded, sourceData, posActualFlg, selectedRowData, compact, collapsed, open, onToggle }) => {
  const { transformSourceData: { INVENTORY_AGG } } = codeUtil;

  /* posActualFlg가 꺼져 있으면(POS 계열 데이터를 안 쓰기로 한 것) POS History 소스도 목록에서 뺀다. */
  const visibleSourceData = posActualFlg
    ? sourceData
    : sourceData.filter((source) => !includes(sourceTarget.posMeasures, source.title));

  /* 선택된 타겟에 연결된 소스만 강조한다(옛 그리드 행 배경색 하이라이트를 리스트 항목 강조로 옮긴 것). */
  const linkedTitles = useMemo(() => {
    if (isEmpty(selectedRowData)) {
      return new Set();
    }
    const titles = [];
    for (const row of selectedRowData) {
      const { target } = row;
      if (target === INVENTORY_AGG) {
        titles.push(...sourceTarget.inventoryAggMon);
      } else if (target === FINANCE_PLAN) {
        titles.push(...sourceTarget.planMeasures);
      } else if (target === SALES_PLAN || target === SALES_PLAN_MEASURE) {
        titles.push(...sourceTarget.salesPlan);
      } else if (target === SALES_ORDER) {
        titles.push(...sourceTarget.salesOrder);
      } else if (target === POS_ACTUAL_SALES || target === POS_ACTUAL_SALES_YOY || target === POS_AVG_MEASURE) {
        titles.push(...sourceTarget.posMeasures);
      } else {
        titles.push(...sourceTarget.defaultMeasures);
      }
    }
    return new Set(titles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowData]);

  /*
   * 접힘 폭에서 펼칠 때는 자리를 차지하지 않�� 캔버스 위에 겹쳐 뜬다.
   * 자리를 차지하고 들어가면 방금 표에 넘긴 폭을 도로 가져가 표가 다시 가로 스크롤이 되고,
   * 열고 닫을 때마다 표가 60px 씩 흔들린다. 잠깐 참고하고 닫는 자리라 겹치는 편이 맞다.
   */
  const panel = (
    <AppCard sx={{
      width: compact ? SOURCE_CARD_WIDTH_COMPACT : SOURCE_CARD_WIDTH,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      p: 2,
      ...(collapsed && { position: "absolute", zIndex: 2, top: 16, bottom: 16, left: 16, boxShadow: 6 }),
    }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
        <Typography sx={SECTION_TITLE_SX}>{t("sourceData")}</Typography>
        <Typography sx={{ ...DETAIL_TEXT_SX, ml: "auto" }}>{visibleSourceData.length}</Typography>
        {collapsed && (
          <IconButton size="small" sx={{ ml: 0.5 }} title={t("close")} onClick={() => onToggle(false)}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box id="data-transform-source-list" sx={{ flex: 1, minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
        {isEmpty(visibleSourceData) && <EmptyList loaded={loaded} label={t("MSG_NoData")} />}
        {visibleSourceData.map((source) => {
          const linked = linkedTitles.has(source.title);
          return (
            <Box
              key={source.title}
              sx={{
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                boxSizing: "border-box",
                border: 1,
                /* 한 화면에서 "골랐다/이어졌다"는 항상 primary 틴트로만 말한다 — 타겟 행 선택(아래)과 같은 색 계열.
                   MUI 팔레트 색(lightBlue)을 직접 쓰면 브랜드 teal 과 계열이 달라 이 카드만 따로 논다. */
                bgcolor: linked ? (theme) => alpha(theme.palette.primary.main, 0.08) : "grey.50",
                borderColor: linked ? "primary.main" : "transparent",
              }}
            >
              <Typography sx={CARD_HEADER_SX}>{t(source.title)}</Typography>
              <Typography sx={{ ...DETAIL_TEXT_SX, mt: 0.25 }}>
                {dateUtils.formatDateTime(source.latestDate) || "—"}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </AppCard>
  );

  if (!collapsed) {
    return panel;
  }

  /*
   * 접힌 상태 — 세로 띠는 늘 자리에 남기고, 펼친 카드만 그 위에 겹친다.
   * 완전히 감추지 않는 건 이 카드가 "지금 어떤 소스가 언제 들어와 있나"를 보는 유일한 자리라서다.
   * 띠에 개수를 남겨 두면 접혀 있어도 목록이 살아 있다는 게 보인다.
   */
  return (
    <>
      <AppCard sx={{ width: SOURCE_STRIP_WIDTH, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, py: 1.5, px: 0 }}>
        <IconButton size="small" title={t("sourceData")} onClick={() => onToggle(true)}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ ...DETAIL_TEXT_SX, fontSize: FS.SUB }}>{visibleSourceData.length}</Typography>
      </AppCard>
      {open && panel}
    </>
  );
};

const TargetDataCard = ({ t, loaded, buckets, planningRange, startDay, bucketUnit, avgMonths, posActualFlg, targetData, sourceData, selectedRowData, userRoles, onRun, progressOn, compact, dense }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [targetDatas, setTargetDatas] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [itemLevelCds, setItemLevelCds] = useState([]);
  const [custLevelCds, setCustLevelCds] = useState([]);
  const [planVersionList, setPlanVersionList] = useState([]);

  /* 열 비율과 최소폭은 배치에 따라 갈린다 — 헤더와 각 행이 같은 값을 봐야 줄이 어긋나지 않는다. */
  const cols = compact ? COLS_COMPACT : COLS;
  const tableMinWidth = dense ? TABLE_MIN_WIDTH_DENSE : (compact ? TABLE_MIN_WIDTH_COMPACT : TABLE_MIN_WIDTH);

  const optionSelect = [
    { value: "deleteAndAdd", text: t("deleteAndAdd") },
    { value: "delete", text: t("delete") }
  ];

  /* 값은 개월 수(숫자)로만 들고, 보이는 글자는 그릴 때 붙인다 — 언어를 바꿔도 값은 그대로다. */
  const avgMonthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  const addOption = [{ value: "add", text: t("add") }]

  const getPeriod = (target, salesDate, measure) => {
    let maxD = target.latestDate ?? new ZDate();
    let period = {};
    if (target.title === demandTitle || target.title === salePlanTitle) {
      if (measure === "actualSales" || measure === "posActualSales") {
        const actualSalesMeasureCd = measure === "posActualSales" ? POS_SA_QTY.cd : "SA_QTY";
        if (target.measureDate?.[actualSalesMeasureCd]) {
          maxD = new ZDate(target.measureDate[actualSalesMeasureCd]);
        } else {
          maxD = salesDate ?? new ZDate();
        }
        if (salesDate > maxD) {
          if (codeUtil.isWeekBucket(target.uom)) {
            period = {
              "startDate": dateUtils.calculateRangeDate(new ZDate(maxD.getFullYear(), maxD.getMonth(), maxD.getDate() - maxD.getDay() + 1, 0, 0, 0), bucketUnit, startDay, 1),
              "endDate": dateUtils.calculateRangeDate(new ZDate(maxD.getFullYear(), maxD.getMonth(), maxD.getDate() - maxD.getDay() + 1, 0, 0, 0), bucketUnit, startDay, planningRange, true)
            };
          } else {
            period = {
              "startDate": dateUtils.calculateRangeDate(new ZDate(maxD.getFullYear(), maxD.getMonth(), 1, 0, 0, 0), bucketUnit, startDay, 1),
              "endDate": dateUtils.calculateRangeDate(new ZDate(maxD.getFullYear(), maxD.getMonth(), 1, 0, 0, 0), bucketUnit, startDay, planningRange, true)
            };
          }
        } else {
          if (codeUtil.isWeekBucket(target.uom)) {
            period = {
              "startDate": dateUtils.calculateRangeDate(new ZDate(maxD.getFullYear(), maxD.getMonth(), maxD.getDate() - maxD.getDay() + 1, 0, 0, 0), bucketUnit, startDay, -planningRange + 1),
              "endDate": new ZDate(maxD.getFullYear(), maxD.getMonth(), maxD.getDate() - maxD.getDay() + 1, 0, 0, 0)
            };
          } else {
            period = {
              "startDate": dateUtils.calculateRangeDate(new ZDate(maxD.getFullYear(), maxD.getMonth(), 1, 0, 0, 0), bucketUnit, startDay, -planningRange + 1),
              "endDate": new ZDate(maxD.getFullYear(), maxD.getMonth(), 1, 0, 0, 0)
            };
          }
        }
      } else {
        let minD = new ZDate();
        if (target?.measureDate) {
          minD = target.measureDate?.YOY_QTY ? new ZDate(target.measureDate.YOY_QTY) : new ZDate();
          for (const cd of [FF_QTY.cd, YOY_QTY.cd, MF_QTY.cd, POS_YOY_QTY.cd, POS_ASA_QTY.cd]) {
            if (target.measureDate[cd] && minD > new ZDate(target.measureDate[cd])) {
              minD = new ZDate(target.measureDate[cd]);
            }
          }
        }
        period = {
          "startDate": dateUtils.calculateRangeDate(minD, bucketUnit, startDay, 1),
          "endDate": dateUtils.calculateRangeDate(minD, bucketUnit, startDay, planningRange, true)
        };
      }
    } else {
      period = {
        "startDate": new ZDate(maxD.getFullYear(), maxD.getMonth(), 1, 0, 0, 0),
        "endDate": new ZDate(maxD.getFullYear(), maxD.getMonth() + 1, 0, 0, 0, 0)
      };
    }
    return period;
  }

  /* 선택은 행 키(title:target) 집합으로만 들고, 실제 행 객체는 파생해서 부모에 올린다.
     행을 편집하면 부모가 쥔 선택 행도 최신 값이어야 하므로 targetDatas 도 의존성에 넣는다. */
  const toggleRow = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  const toggleAll = (checked) => {
    setSelectedKeys(checked ? new Set(targetDatas.map(rowKeyOf)) : new Set());
  }

  const updateRow = (key, field, value) => {
    setTargetDatas(prev => prev.map(row => (rowKeyOf(row) === key ? { ...row, [field]: value } : row)));
  }

  /*
   * 확인창이 그릴 요약 — 코드값이 아니라 **표에 보이는 그 글자**를 만들어 같이 올린다.
   *
   * 옵션·레벨·버전 목록은 여기서 조회한 것이라(planVersionList·custLevelCds·itemLevelCds),
   * 부모가 코드값만 받아 라벨을 다시 붙이려면 그 목록을 한 벌 더 들고 있어야 한다.
   * 그러면 표와 확인창이 서로 다른 글자를 보여 주는 날이 온다 — 라벨은 한 곳에서만 만든다.
   *
   * 행에 칸을 하나 더 얹지만 getRequestParams 는 이름으로 골라 읽으므로 전송값은 그대로다.
   */
  const labelOf = (list, value) => list.find(o => (o.value ?? o.cd) === value)?.text
    ?? list.find(o => (o.value ?? o.cd) === value)?.name
    ?? null;

  const displayInfoOf = (row) => {
    const hasPeriod = !isEmpty(row.startDate) || row.startDate instanceof Date;
    const dateFormat = isMonthPicker(row) ? "yyyy-MM" : "yyyy-MM-dd";
    /* formatDate 가 안에서 ZDate 로 감싸므로 여기서 또 감싸지 않는다 */
    const fmt = (v) => (v ? dateUtils.formatDate(v, dateFormat) : null);
    return {
      name: targetNameOf(t, row),
      uom: isUomEditable(row) ? avgUomLabel(t, row.avgMonths) : t(row.uom),
      startDate: hasPeriod ? fmt(row.startDate) : null,
      endDate: hasPeriod ? fmt(row.endDate) : null,
      option: labelOf(optionsOf(row), row.option),
      /* 레벨은 고를 수 있는 행에서만 뜻이 있다 — 나머지 행은 표에서도 대시라 요약에서도 뺀다 */
      custLevel: isLevelEditable(row) ? labelOf(custLevelCds, row.custLevel) : null,
      itemLevel: isLevelEditable(row) ? labelOf(itemLevelCds, row.itemLevel) : null,
    };
  };

  useEffect(() => {
    selectedRowData(targetDatas
      .filter(row => selectedKeys.has(rowKeyOf(row)))
      .map(row => ({ ...row, displayInfo: displayInfoOf(row) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys, targetDatas, planVersionList, custLevelCds, itemLevelCds]);

  /* 편집 가능 규칙은 옛 setEditableFlg 를 그대로 옮긴 것 —
     기간단위는 평균 판매실적 행에서만, 고객/품목 레벨은 재무계획·판매계획 계열에서만 고를 수 있다. */
  const isUomEditable = (row) => row.target === AVG_MEASURE || row.target === POS_AVG_MEASURE;
  const isLevelEditable = (row) => row.target === FINANCE_PLAN || row.target === SALES_PLAN || row.target === SALES_PLAN_MEASURE;

  /* 달 버킷이면 연·월까지만 고르게 한다(옛 getDateType 의 dateType 분기와 같은 기준). */
  const isMonthPicker = (row) => row.uom === buckets.MONTH;

  /*
   * 주 버킷에서는 아무 날이나 고르면 버킷 경계가 깨진다 —
   *   시작일은 주 시작요일만, 종료일은 그 전날(주 마지막 날)만 유효하다.
   *   PARTIAL_WEEK 이면 월 경계(시작=1일 / 종료=말일)도 함께 허용한다.
   * 옛 GridDatePicker 의 filterDate(isWeekday) 규칙을 그대로 옮긴 것이다.
   */
  const isSelectableDate = (row, isStart, date) => {
    if (isMonthPicker(row) || !date) {
      return true;
    }
    if (bucketUnit === codeUtil.buckets.PARTIAL_WEEK) {
      if (isStart && date.getDate() === 1) {
        return true;
      }
      if (!isStart && date.getDate() === dateUtils.endOfMonthDate(date).getDate()) {
        return true;
      }
    }
    const weekDayCd = isStart ? startDay : dateUtils.getStartDayByRange(startDay, -1);
    return date.getDay() === dateUtils.getMyDayId(weekDayCd);
  }

  /* 달 버킷은 시작·종료 모두 그 달 1일로 맞춘다(옛 getDateType 이 양쪽에 isFirstDay:true 를 준 것과 같다). */
  const snapDate = (row, value) => {
    if (!value || !isMonthPicker(row)) {
      return value;
    }
    return dateUtils.startOfMonthDate(value);
  }

  /* 옵션 목록: 평균 판매실적만 계획 버전 목록을 쓰고, 재무·판매계획 계열에는 "추가"가 하나 더 붙는다. */
  const optionsOf = (row) => {
    if (row.target === AVG_MEASURE || row.target === POS_AVG_MEASURE) {
      return planVersionList.map(obj => ({ value: obj.name, text: obj.name }));
    }
    return isLevelEditable(row) ? [...optionSelect, ...addOption] : optionSelect;
  }

  const getTargetData = (salesDate) => {
    const datas = [];

    if (targetData.length <= 0) {
      setTargetDatas([]);
      return;
    }
    let cloneTotalMeasures = [];
    if (includes(userRoles, APP_DP)) {
      cloneTotalMeasures = cloneDeep(dpTargetMeasures);
      if (!posActualFlg) {
        for (const measure of Object.keys(cloneTotalMeasures)) {
          if (includes(posTargetMeasureCds, measure)) {
            delete cloneTotalMeasures[measure];
          }
        }
      }
    }

    for (const target of targetData) {
      if (isEqual(target.title, demandTitle)) {
        for (const measure of Object.keys(cloneTotalMeasures)) {
          const name = cloneTotalMeasures[measure];
          let row = {
            title: target.title,
            target: name,
          }
          if (measure === ASA_QTY.cd || measure === POS_ASA_QTY.cd) {
            row = {
              ...row,
              /* 평균 개월 수는 숫자로 들고 화면에서만 "3개월"로 그린다 — 문자열로 굳히면 언어를 바꿨을 때
                 드롭다운 목록(새 언어)에 없는 값이 되어 빈칸으로 보인다. */
              avgMonths, //plan config
              latestDate:"",
              startDate: "",
              endDate: "",
              option: planVersionList ? planVersionList?.[0]?.name : ""
            }
          } else {
            const periods = getPeriod(target, salesDate, name);
            row = { ...row,
              uom: target?.uom ?? buckets.MONTH,
              latestDate: target.measureDate?.[measure] ? new ZDate(target.measureDate[measure]) : target.latestDate,
              ...periods,
              option: optionSelect[0].value
            }
            if (row.target === FINANCE_PLAN || row.target === SALES_PLAN_MEASURE) {
              row.custLevel = custLevelCds ? custLevelCds[0]?.cd : "";
              row.itemLevel = itemLevelCds ? itemLevelCds[0]?.cd : "";
            }
          }
          datas.push(row);
        }
      } else if (isEqual(target.title, salePlanTitle)) {
        if (includes(userRoles, APP_DP)) {
          continue;
        }
        const periods = getPeriod(target, salesDate, salePlanTitle);
        const row = {
          title: target.title,
          target: salePlanTitle,
          uom: target?.uom ?? buckets.MONTH,
          latestDate: target.measureDate?.MF_QTY ? new ZDate(target.measureDate.MF_QTY) : target.latestDate,
          ...periods,
          option: optionSelect[0].value
        }
        row.custLevel = custLevelCds ? custLevelCds[0].cd : "";
        row.itemLevel = itemLevelCds ? itemLevelCds[0].cd : "";

        datas.push(row);
      } else {
        const periods = getPeriod(target, salesDate, null);
        const row = {
          title: target.title,
          target: target.title,
          uom: target?.uom ?? buckets.MONTH,
          latestDate: target.latestDate,
          ...periods,
          option: optionSelect[0].value
        }
        datas.push(row);
      }
    }
    const dataSet = uniq(datas);
    const data = [...dataSet];
    setTargetDatas(data);
  }

  const retrievePlanVersion = () => {
    planVersionService.getAll({ moduleCd: codeUtil.module.DP, statusCds: [SCENARIO], orderByColumn: "createdTs", sortType: "desc", includeDefault: false})
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          return;
        }
        setPlanVersionList(results)
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveHrchyConfig = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }

            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            } else {
              custConfig.push(row);
            }
          }
          setItemLevelCds([...itemConfig, item]);
          if (!includes(userRoles, APP_DP)) {
            setCustLevelCds([customer]);
          } else {
            setCustLevelCds([...custConfig, customer]);
          }
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  useEffect(() => {
    setIsLoading(true);
    retrieveHrchyConfig();
    retrievePlanVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  useEffect(() => {
    /* posActualFlg는 getPeriodUom()이 비동기로 채우므로 targetData보다 늦게 도착할 수 있다.
       posActualFlg만 바뀌어도 다시 계산해야 POS measure 유무가 타깃 카드에 반영된다. */
    if (targetData.length > 0) {
      let salesLastDate = new ZDate();
      for (const row of sourceData) {
        if (row.title === "salesTransaction") {
          salesLastDate = row.latestDate;
        }
      }
      getTargetData(salesLastDate);
      if (isLoading) {
        setIsLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, targetData, posActualFlg]);

  return (
    /* AppCard 기본값은 overflow:hidden 이지만, 마지막 행에서 열리는 셀 편집 팝업(옵션·레벨 드롭다운,
       날짜 선택기)이 카드 경계에서 잘리므로 이 카드만 열어둔다. 그리드는 flex 로 이미 카드 안에 갇혀 있어 넘치지 않는다. */
    <AppCard sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", p: 2, overflow: "visible" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <Typography sx={SECTION_TITLE_SX}>{t("targetData")}</Typography>
        {/* 개수·선택 수는 제목 옆에 둔다 — "무엇이 몇 개 있고 몇 개 골랐는지"는 목록의 성질이라 제목에 딸린다. */}
        <Typography sx={DETAIL_TEXT_SX}>
          {`${targetDatas.length} · ${selectedKeys.size} ${t("selected")}`}
        </Typography>
      </Box>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box id="data-transform-target-list" sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {/* 행이 없으면 표 자체를 걷는다 — 고를 것도 없는데 헤더와 전체선택 체크박스만 남으면 고장으로 읽힌다. */}
          {isEmpty(targetDatas) ? <EmptyList loaded={loaded} label={t("MSG_NoData")} /> : (
          <Box sx={{ minWidth: tableMinWidth }}>
            {/* 헤더 — 값보다 물러난 라벨 한 줄. 격자선 없이 아래 헤어라인만 둔다. */}
            <Box sx={{ ...ROW_SX, minHeight: dense ? 44 : 36, borderColor: "divider" }}>
              <Box sx={{ width: cols.check }}>
                <Checkbox
                  size="small"
                  sx={{ p: 0.5 }}
                  checked={targetDatas.length > 0 && selectedKeys.size === targetDatas.length}
                  indeterminate={selectedKeys.size > 0 && selectedKeys.size < targetDatas.length}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </Box>
              <Typography sx={{ ...HEADER_LABEL_SX, ...FLEX_CELL_SX, flex: cols.name }}>{t("targetName")}</Typography>
              <Typography sx={{ ...HEADER_LABEL_SX, ...FLEX_CELL_SX, flex: cols.uom }}>{t("periodUom")}</Typography>
              {/* 날짜를 한 열에 포갠 배치에서는 헤더도 같은 순서로 두 줄이 된다 —
                  위가 시작, 아래가 종료. 라벨을 하나로 합치면 어느 줄이 어느 쪽인지 알 수 없다. */}
              {dense ? (
                <Box sx={DATE_CELL_SX}>
                  <Typography sx={HEADER_LABEL_SX}>{t("startDate")}</Typography>
                  <Typography sx={HEADER_LABEL_SX}>{t("endDate")}</Typography>
                </Box>
              ) : (
                <>
                  <Typography sx={{ ...HEADER_LABEL_SX, ...DATE_CELL_SX }}>{t("startDate")}</Typography>
                  <Typography sx={{ ...HEADER_LABEL_SX, ...DATE_CELL_SX }}>{t("endDate")}</Typography>
                </>
              )}
              <Typography sx={{ ...HEADER_LABEL_SX, ...FLEX_CELL_SX, flex: cols.option }}>{t("option")}</Typography>
              <Typography sx={{ ...HEADER_LABEL_SX, ...FLEX_CELL_SX, flex: cols.cust }}>{t("custLevel")}</Typography>
              <Typography sx={{ ...HEADER_LABEL_SX, ...FLEX_CELL_SX, flex: cols.item }}>{t("itemLevel")}</Typography>
              {!compact && (
                <Typography sx={{ ...HEADER_LABEL_SX, ...FLEX_CELL_SX, flex: cols.latest, textAlign: "right" }}>{t("latestDate")}</Typography>
              )}
            </Box>

            {targetDatas.map((row) => {
              const key = rowKeyOf(row);
              const selected = selectedKeys.has(key);
              const hasPeriod = !isEmpty(row.startDate) || row.startDate instanceof Date;
              const rowOptions = optionsOf(row);
              const [latestDay, latestTime] = (dateUtils.formatDateTime(row.latestDate) || "").split(" ");
              const monthOnly = isMonthPicker(row);
              const dateViews = monthOnly ? ["year", "month"] : ["year", "month", "day"];
              const dateFormat = monthOnly ? "yyyy-MM" : "yyyy-MM-dd";

              /* 시작·종료는 고르는 값만 다르고 나머지가 같다. 배치에 따라 두 열이 되기도 한 열이 되기도
                 해서 두 군데에서 그리므로, 그리는 법은 한 곳에 둔다. */
              const datePickerFor = (isStart) => (
                <DatePicker
                  views={dateViews}
                  format={dateFormat}
                  value={(isStart ? row.startDate : row.endDate) || null}
                  shouldDisableDate={(date) => !isSelectableDate(row, isStart, date)}
                  onChange={(value) => updateRow(key, isStart ? "startDate" : "endDate", snapDate(row, value))}
                  slotProps={{ textField: { variant: "standard", size: "small", sx: PERIOD_DATE_SX } }}
                />
              );

              return (
                <Box
                  key={key}
                  sx={{
                    ...ROW_SX,
                    /* 날짜 두 개를 포개면 48px 안에 안 들어간다 — 행을 그만큼만 키운다. */
                    minHeight: dense ? 62 : ROW_SX.minHeight,
                    bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.06) : "transparent",
                    "&:hover": {
                      bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.09) : "action.hover",
                    },
                  }}
                >
                  <Box sx={{ width: cols.check }}>
                    <Checkbox size="small" sx={{ p: 0.5 }} checked={selected} onChange={() => toggleRow(key)} />
                  </Box>

                  {/* 좁은 배치에서는 최신일자가 열이 아니라 이름 밑 캡션으로 붙는다.
                      시각은 떼고 날짜만 남긴다 — 좁은 폭에서 둘 다는 안 들어가고, 훑을 땐 날짜만 보면 된다.
                      떼어낸 시각은 title 로 남겨 hover 하면 그대로 보인다. */}
                  <Box sx={{ ...FLEX_CELL_SX, flex: cols.name, pl: CELL_INSET, pr: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: FS.H2, fontWeight: VALUE_WEIGHT, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {targetNameOf(t, row)}
                    </Typography>
                    {compact && latestDay && (
                      <Typography
                        title={`${t("latestDate")} ${latestDay}${latestTime ? ` ${latestTime}` : ""}`}
                        sx={{ fontSize: FS.CAPTION, fontWeight: VALUE_WEIGHT, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {`${t("latestDate")} ${latestDay}`}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ ...FLEX_CELL_SX, flex: cols.uom }}>
                    {isUomEditable(row) ? (
                      <Select variant="standard" value={row.avgMonths ?? ""} sx={QUIET_SELECT_SX}
                        onChange={(e) => updateRow(key, "avgMonths", e.target.value)}>
                        {avgMonthOptions.map(opt => <MenuItem key={opt} value={opt}>{avgUomLabel(t, opt)}</MenuItem>)}
                      </Select>
                    ) : (
                      <Typography sx={{ fontSize: FS.TITLE, fontWeight: VALUE_WEIGHT, color: "text.primary", pl: CELL_INSET }}>{t(row.uom)}</Typography>
                    )}
                  </Box>

                  {/*
                    시작·종료를 각자 열로 나눠 같은 시점끼리 세로로 대조되게 한다.
                    두 열이 안 들어가는 폭에서는 한 열에 위아래로 포갠다 — 날짜만은 폭이
                    'yyyy-MM-dd' 로 고정이라 좁힐 수가 없고(좁히면 달력 아이콘이 잘린다),
                    포개면 128px 을 통째로 돌려받아 나머지 열이 눌리지 않는다.
                  */}
                  {dense ? (
                    <Box sx={{ ...DATE_CELL_SX, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      {hasPeriod ? (
                        <>
                          {datePickerFor(true)}
                          {datePickerFor(false)}
                        </>
                      ) : <Dash />}
                    </Box>
                  ) : (
                    <>
                      <Box sx={DATE_CELL_SX}>
                        {hasPeriod ? datePickerFor(true) : <Dash />}
                      </Box>
                      <Box sx={DATE_CELL_SX}>
                        {hasPeriod ? datePickerFor(false) : <Dash />}
                      </Box>
                    </>
                  )}

                  <Box sx={{ ...FLEX_CELL_SX, flex: cols.option }}>
                    {/* 평균 판매실적의 옵션 목록은 DP 계획 버전이라, 버전이 없으면 고를 게 없다 —
                        그때는 빈 드롭다운 대신 대시를 둔다. */}
                    {isEmpty(rowOptions) ? <Dash /> : (
                      <Select variant="standard" value={row.option ?? ""} sx={OPTION_CHIP_SX}
                        onChange={(e) => updateRow(key, "option", e.target.value)}>
                        {rowOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.text}</MenuItem>)}
                      </Select>
                    )}
                  </Box>

                  <Box sx={{ ...FLEX_CELL_SX, flex: cols.cust }}>
                    {isLevelEditable(row) ? (
                      <Select variant="standard" value={row.custLevel ?? ""} sx={QUIET_SELECT_SX}
                        onChange={(e) => updateRow(key, "custLevel", e.target.value)}>
                        {custLevelCds.map(lv => <MenuItem key={lv.cd} value={lv.cd}>{lv.name}</MenuItem>)}
                      </Select>
                    ) : <Dash />}
                  </Box>

                  <Box sx={{ ...FLEX_CELL_SX, flex: cols.item }}>
                    {isLevelEditable(row) ? (
                      <Select variant="standard" value={row.itemLevel ?? ""} sx={QUIET_SELECT_SX}
                        onChange={(e) => updateRow(key, "itemLevel", e.target.value)}>
                        {itemLevelCds.map(lv => <MenuItem key={lv.cd} value={lv.cd}>{lv.name}</MenuItem>)}
                      </Select>
                    ) : <Dash />}
                  </Box>

                  {/* 날짜와 시각을 두 줄로 나눈다 — 한 줄이면 좁은 열에서 잘리고, 훑을 땐 날짜만 보면 된다. */}
                  {!compact && (
                    <Box sx={{ ...FLEX_CELL_SX, flex: cols.latest, textAlign: "right" }}>
                      {latestDay ? (
                        <>
                          <Typography sx={{ fontSize: FS.TITLE, fontWeight: VALUE_WEIGHT, color: "text.secondary" }}>{latestDay}</Typography>
                          {latestTime && (
                            <Typography sx={{ fontSize: FS.SUB, fontWeight: VALUE_WEIGHT, color: "text.secondary" }}>{latestTime}</Typography>
                          )}
                        </>
                      ) : <Dash />}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          )}
        </Box>
      </LocalizationProvider>

      {/*
        * 실행 줄 — 카드 맨 아래에 고정한다.
        * 목록(위)이 flex:1 로 남는 높이를 다 먹으므로 이 줄은 스크롤 영역 밖에서 바닥에 남는다.
        * 행이 많아 스크롤이 생겨도 버튼은 늘 같은 자리에 보인다.
        * 왼쪽 정렬은 앱의 주 실행/저장 버튼 자리와 같다(DP Settings 저장, DP Process Management 실행).
        */}
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0, mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Button variant="contained"
          disabled={progressOn}
          /* 아이콘은 왼쪽 — 앱의 다른 실행 버튼(DP Process Management, AI 화면)과 같은 자리에 둔다. */
          startIcon={progressOn ? <CircularProgress size={20} /> : <StartIcon />}
          size="medium"
          color={progressOn ? "error" : "info"}
          /*
           * 폭은 글자에 맡긴다 — 라벨 길이가 언어마다 크게 다르다(중국어 "执行转换" 4자 ↔
           * 베트남어 "Chạy Chuyển Đổi Dữ Liệu" 23자). 고정 폭이면 한쪽은 텅 비고 한쪽은 넘친다.
           * nowrap 으로 두 줄이 되는 것만 막고, 최소 폭은 MUI 기본 64px 에 맡긴다.
           * 스피너를 아이콘과 같은 20px 로 맞춰 실행 중에 버튼 폭이 흔들리지 않게 한다.
           */
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
          onClick={onRun}
        >
          {t("runTransform")}
        </Button>
      </Box>
    </AppCard>
  )
}

export default withTranslation()(DataTransform);
